import React, { useState, useEffect } from 'react';
import { Text } from './ui';
import sessionReportService from '../services/sessionReportService';
import { useAuth } from '../context/authContext';

const colorTokens = {
    alabaster: '#EEEEE7',
    white: '#FFFFFF',
    oxfordBlue: '#1b263b',
    richBlack: '#0d1b2a',
    neutral: {
        0: '#FFFFFF',
        50: '#F9F8F6',
        900: '#0D0D0D'
    }
};

const SessionDetailModal = ({ isOpen, onClose, post }) => {
    const { user } = useAuth();
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [sessionReport, setSessionReport] = useState(null);
    const [loadingReport, setLoadingReport] = useState(false);
    
    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }
        
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [isOpen]);
    
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);
    
    // Fetch session report when modal opens
    useEffect(() => {
        const fetchSessionReport = async () => {
            if (!isOpen || !post || !post.session_id || !user) return;
            
            setLoadingReport(true);
            setSessionReport(null); // Reset on each open
            
            try {
                const token = await user.getIdToken();
                const result = await sessionReportService.getSessionReport(post.session_id, token);
                if (result && result.data) {
                    setSessionReport(result.data);
                }
            } catch (error) {
                // Session report not available (could be friend's post or missing data)
                // This is expected for friend posts, so we'll just show what we have
                setSessionReport(null);
            } finally {
                setLoadingReport(false);
            }
        };
        
        fetchSessionReport();
    }, [isOpen, post, user]);
    
    if (!isOpen || !post) return null;
    
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
        
        if (diffInHours < 1) {
            const diffInMinutes = Math.floor((now - date) / (1000 * 60));
            return diffInMinutes === 1 ? '1 minute ago' : `${diffInMinutes} minutes ago`;
        } else if (diffInHours < 24) {
            return diffInHours === 1 ? '1 hour ago' : `${diffInHours} hours ago`;
        } else {
            const diffInDays = Math.floor(diffInHours / 24);
            if (diffInDays < 7) {
                return diffInDays === 1 ? '1 day ago' : `${diffInDays} days ago`;
            } else {
                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            }
        }
    };
    
    // Collect all images
    const allImages = [];
    
    if (post.final_image) {
        allImages.push({
            ...post.final_image,
            type: 'final',
            caption: post.final_image.caption || 'Final dish'
        });
    }
    
    if (post.progress_photos && post.progress_photos.length > 0) {
        post.progress_photos.forEach(photo => {
            allImages.push({
                ...photo,
                type: 'progress'
            });
        });
    }
    
    if (allImages.length === 0 && post.recipe_images && Array.isArray(post.recipe_images) && post.recipe_images.length > 0) {
        post.recipe_images.forEach((imageUrl, index) => {
            if (typeof imageUrl === 'string') {
                allImages.push({
                    url: imageUrl,
                    type: 'recipe',
                    caption: `Recipe photo ${index + 1}`,
                    timestamp: new Date().toISOString()
                });
            }
        });
    }
    
    const currentImage = allImages[currentImageIndex] || null;
    
    const nextImage = () => {
        if (allImages.length > 1) {
            setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
        }
    };
    
    const prevImage = () => {
        if (allImages.length > 1) {
            setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
        }
    };
    
    const displayName = post.user?.display_name || post.user?.username || 'Unknown User';
    
    return (
        <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            onClick={onClose}
        >
            <div 
                className="bg-neutral-0 hairline max-w-3xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 hairline-b">
                    <div className="flex items-center gap-3">
                        <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center font-mono text-sm font-medium"
                            style={{ 
                                backgroundColor: colorTokens.neutral[900],
                                color: colorTokens.neutral[0]
                            }}
                        >
                            {displayName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                            <Text className="font-medium">{displayName}</Text>
                            <Text className="text-sm opacity-60">cooked {post.recipe_title}</Text>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-neutral-900 hover:opacity-60 transition-opacity duration-120 font-mono text-lg border-none bg-transparent p-0 m-0 modal-close"
                    >
                        ✕
                    </button>
                </div>
                
                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Caption */}
                    {post.caption && (
                        <div>
                            <Text className="leading-relaxed">{post.caption}</Text>
                        </div>
                    )}
                    
                    {/* Images */}
                    {allImages.length > 0 && currentImage && (
                        <div>
                            <div className="relative">
                                <img
                                    src={currentImage.url}
                                    alt={currentImage.caption || 'Cooking photo'}
                                    className="w-full h-96 object-cover hairline"
                                />
                                
                                {allImages.length > 1 && (
                                    <>
                                        <button
                                            onClick={prevImage}
                                            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-70 transition-opacity"
                                        >
                                            ‹
                                        </button>
                                        <button
                                            onClick={nextImage}
                                            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-70 transition-opacity"
                                        >
                                            ›
                                        </button>
                                        <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1">
                                            {currentImageIndex + 1} / {allImages.length}
                                        </div>
                                    </>
                                )}
                            </div>
                            
                            {currentImage.caption && (
                                <Text className="text-sm opacity-80 mt-2">{currentImage.caption}</Text>
                            )}
                        </div>
                    )}
                    
                    {/* Timeline and Additional Notes */}
                    {!loadingReport && sessionReport && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Timeline */}
                            <div className="bg-neutral-50 hairline p-4">
                                <Text className="font-mono text-xs uppercase tracking-wider opacity-60 mb-4">Timeline</Text>
                                {sessionReport.timeline && sessionReport.timeline.length > 0 ? (
                                    <div className="space-y-3">
                                        {sessionReport.timeline.map((entry, index) => (
                                            <div key={entry.id || index} className="text-sm">
                                                <div className="flex items-start gap-2">
                                                    <div 
                                                        className="w-2 h-2 mt-1.5 flex-shrink-0"
                                                        style={{ backgroundColor: colorTokens.oxfordBlue }}
                                                    ></div>
                                                    <div className="flex-1">
                                                        {entry.step_title && (
                                                            <Text className="font-medium text-sm mb-1">{entry.step_title}</Text>
                                                        )}
                                                        {entry.notes && (
                                                            <Text className="text-sm opacity-80">{entry.notes}</Text>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <Text className="text-sm opacity-60 italic">No timeline notes recorded</Text>
                                )}
                            </div>
                            
                            {/* Additional Notes */}
                            <div className="bg-neutral-50 hairline p-4">
                                <Text className="font-mono text-xs uppercase tracking-wider opacity-60 mb-4">Additional Notes</Text>
                                {sessionReport.additional_notes ? (
                                    <Text className="text-sm leading-relaxed whitespace-pre-wrap">{sessionReport.additional_notes}</Text>
                                ) : (
                                    <Text className="text-sm opacity-60 italic">No additional notes recorded</Text>
                                )}
                            </div>
                        </div>
                    )}
                    
                    {!loadingReport && !sessionReport && (
                        <div className="bg-neutral-50 hairline p-6 text-center">
                            <Text className="text-sm opacity-60 italic">
                                Session details not available
                            </Text>
                        </div>
                    )}
                    
                    {loadingReport && (
                        <div className="text-center py-8">
                            <Text className="text-sm opacity-60">Loading session details...</Text>
                        </div>
                    )}
                    
                    {/* Session Info */}
                    <div className="bg-neutral-50 hairline p-4">
                        <div className="flex items-center justify-between mb-3">
                            <Text className="font-mono text-xs uppercase tracking-wider opacity-60">Session Info</Text>
                            <Text className="text-sm opacity-60">{formatDate(post.posted_at)}</Text>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            {post.started_at && (
                                <div>
                                    <Text className="text-xs font-mono uppercase tracking-wider opacity-60 mb-1">Started</Text>
                                    <Text className="text-sm">{formatDate(post.started_at)}</Text>
                                </div>
                            )}
                            
                            {post.actual_duration_seconds && (
                                <div>
                                    <Text className="text-xs font-mono uppercase tracking-wider opacity-60 mb-1">Duration</Text>
                                    <Text className="text-sm">{Math.floor(post.actual_duration_seconds / 60)} minutes</Text>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SessionDetailModal;

